// The back arrow in a header, for a screen that may have been opened directly.
//
// `navigate(-1)` is "undo my last navigation", and it quietly does nothing when
// there was no last navigation. That is not a rare case here: an SMS link, a
// push notification, a shared fund link, a bookmark and a mini-app launch all
// drop a member straight onto a deep screen with an empty history — and on
// every one of those the arrow in the corner was dead. Tapping it did not
// misbehave, which is worse: it gave no sign at all, so the member taps it
// again, then goes looking for another way out.
//
// Where the arrow did reach a previous entry it was the messenger or the page
// the member came from, so back walked them out of Hamyan entirely — inside a
// mini app that reads as the app crashing.
//
// So: one screen back when there is one, and otherwise the screen this one
// hangs under.
//
// `replace`, not a push. Pushing the fallback leaves the orphan screen
// underneath it, so the next back lands on it again and its arrow sends them
// home again — the same two screens forever, which is the loop already
// documented in modules/miniapp/backButton.ts.

import { useNavigate } from 'react-router-dom'

import { historyIndex } from './flow'

/**
 * Is there a screen of ours behind this one?
 *
 * Read from react-router's history index, the same source the mini-app back
 * key uses. An entry we pushed carries an `idx` above 0; the first entry of a
 * document — a deep link, a reload's landing, a fresh mini-app launch — is 0,
 * and anything behind that belongs to whoever sent the member here.
 */
export const canGoBack = () => historyIndex() > 0

/**
 * @param fallback where to go when there is nothing behind this screen.
 *   Defaults to home; pass the screen this one belongs under when there is a
 *   more useful answer than that (a fund's page, the loan list).
 */
export default function useGoBack(fallback = '/home') {
    const navigate = useNavigate()

    return () => {
        if (canGoBack()) {
            navigate(-1)
            return
        }
        navigate(fallback, { replace: true })
    }
}
